import { Fade, Tooltip } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import clsx from 'clsx';
import { ComponentProps, CSSProperties, ReactElement } from 'react';
import { SVGIconComponent } from '../icons/types';
import { isIconBalanced, StatusBadgeStyleProps, useStatusBadgeStyles } from './status-badge.styles';

export interface StatusBadgeProps extends StatusBadgeStyleProps {
  className?: string;
  style?: CSSProperties;
  label: string;
  color: string;
  fontColor?: string;
  cornerRounding: 'min' | 'med' | 'max';
  collapse?: 'none' | 'wrap-text' | 'wrap-sides';
  capitalize?: boolean;
  showTooltip?: boolean;
  tooltipLabel?: string;
  tooltipPlacement?: ComponentProps<typeof Tooltip>['placement'];
  Icon?: (() => JSX.Element) | SvgIconComponent | SVGIconComponent;
}

export const StatusBadge = ({
  label,
  className,
  tooltipLabel,
  tooltipPlacement = 'left',
  Icon,
  style,
  ...styleProps
}: StatusBadgeProps) => {
  const badgeStyleProps = { ...styleProps, hasIcon: Icon !== undefined };
  const classes = useStatusBadgeStyles(badgeStyleProps);

  const Wrapper = ({ children }: { children: ReactElement<any, any> }) =>
    styleProps.showTooltip ? (
      <Tooltip
        classes={{ tooltip: classes.tooltip }}
        placement={tooltipPlacement}
        title={tooltipLabel || label}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        slots={{
          transition: Fade
        }}
        slotProps={{
          transition: { timeout: 1000 }
        }}
      >
        {children}
      </Tooltip>
    ) : (
      <>{children}</>
    );

  const IconWrapper = ({ children }: { children: ReactElement<any, any> }) =>
    Icon ? (
      <span className={classes.iconWrapper}>
        <Icon className={classes.icon} />
        {children}
        {isIconBalanced(badgeStyleProps) && <span className={classes.iconBalance} />}
      </span>
    ) : (
      <>{children}</>
    );

  return (
    <Wrapper>
      <span style={style} className={clsx(classes.wrapper, className)}>
        <IconWrapper>
          <span className={classes.label}>{label}</span>
        </IconWrapper>
      </span>
    </Wrapper>
  );
};
